import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { Android as AndroidIcon, Apple as AppleIcon } from "@mui/icons-material";
import SmartLinkService from "../services/SmartLinkService";
import type { SmartLink } from "../types/smartLink";
import { getSmartLinkDestination } from "../utils/smartLink";

const PublicSmartLink: React.FC = () => {
  const { slug = "" } = useParams();
  const [link, setLink] = useState<SmartLink | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    SmartLinkService.getPublicLink(slug)
      .then((loadedLink) => {
        if (!active) return;
        if (!loadedLink) {
          setError("This smart link does not exist or is no longer available.");
          return;
        }
        setLink(loadedLink);
        const isTouchMac = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
        const destination = getSmartLinkDestination(
          navigator.userAgent,
          loadedLink.androidStoreUrl,
          loadedLink.iosStoreUrl,
          isTouchMac,
        );
        if (destination.url) window.location.replace(destination.url);
      })
      .catch(() => {
        if (active) setError("We could not open this smart link. Please try again.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [slug]);

  return (
    <Box sx={{ minHeight: "calc(100vh - 48px)", display: "grid", placeItems: "center", px: 2, background: "radial-gradient(circle at top, #eef2ff 0%, #f8fafc 52%, #ffffff 100%)" }}>
      <Card elevation={0} sx={{ width: "100%", maxWidth: 520, borderRadius: 5, border: "1px solid", borderColor: "divider" }}>
        <CardContent sx={{ p: { xs: 3, sm: 5 }, textAlign: "center" }}>
          {loading ? (
            <Stack alignItems="center" spacing={2}><CircularProgress /><Typography color="text.secondary">Opening the right store…</Typography></Stack>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : link ? (
            <>
              <Box sx={{ width: 64, height: 64, borderRadius: 3, display: "grid", placeItems: "center", mx: "auto", mb: 2, color: "primary.main", bgcolor: "primary.50", fontSize: 30, fontWeight: 800 }}>
                {link.appName.slice(0, 1).toUpperCase()}
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>{link.appName}</Typography>
              <Typography color="text.secondary" sx={{ mb: 4 }}>Choose where you would like to download the app.</Typography>
              <Stack spacing={1.5}>
                {link.iosStoreUrl && <Button fullWidth size="large" variant="contained" href={link.iosStoreUrl} startIcon={<AppleIcon />} sx={{ py: 1.4 }}>Download on the App Store</Button>}
                {link.androidStoreUrl && <Button fullWidth size="large" variant="outlined" href={link.androidStoreUrl} startIcon={<AndroidIcon />} sx={{ py: 1.4 }}>Get it on Google Play</Button>}
              </Stack>
            </>
          ) : null}
        </CardContent>
      </Card>
    </Box>
  );
};

export default PublicSmartLink;
